/**
 * Shipping ETA Module
 *
 * Business rules:
 *  - Cutoff: 3 PM PST (America/Los_Angeles)
 *  - Standard/Free: USPS, 5–7 business days
 *  - Priority: FedEx, 3–4 business days
 *  - Express Second Day: FedEx, 2 business days
 *  - Overnight: FedEx, next business day
 *  - Saturday Overnight: FedEx, $70, eligible Thu after cutoff → Fri before cutoff
 *  - Countdown: always visible (except Saturday Overnight). Resets daily at 3 PM PST cutoff.
 */
(function () {
  'use strict';

  /* ====================================================================
     1. CARRIER HOLIDAY CALENDARS
     Format: 'YYYY-MM-DD'
     Update annually.
     ==================================================================== */

  var USPS_HOLIDAYS = [
    /* 2025 */
    '2025-01-01','2025-01-20','2025-02-17','2025-05-26','2025-06-19',
    '2025-07-04','2025-09-01','2025-10-13','2025-11-11','2025-11-27','2025-12-25',
    /* 2026 */
    '2026-01-01','2026-01-19','2026-02-16','2026-05-25','2026-06-19',
    '2026-07-03','2026-09-07','2026-10-12','2026-11-11','2026-11-26','2026-12-25'
  ];

  /* FedEx: Closed = non-business day */
  var FEDEX_CLOSED = [
    /* 2025 */
    '2025-01-01','2025-05-26','2025-07-04','2025-09-01','2025-11-27','2025-12-25',
    /* 2026 */
    '2026-01-01','2026-05-25','2026-07-03','2026-09-07','2026-11-26','2026-12-25'
  ];

  /* FedEx: Modified service = treat conservatively as non-transit day */
  var FEDEX_MODIFIED = [
    /* 2025 */
    '2025-01-20','2025-02-17','2025-06-19','2025-07-05','2025-10-13',
    '2025-11-11','2025-11-28','2025-12-24','2025-12-26','2025-12-31',
    /* 2026 */
    '2026-01-19','2026-02-16','2026-06-19','2026-10-12',
    '2026-11-11','2026-11-27','2026-12-24','2026-12-26','2026-12-31'
  ];

  /* ====================================================================
     2. SHIPPING METHODS CONFIG
     ==================================================================== */

  var METHODS = [
    { id: 'free',      name: 'Free Shipping on Orders $150+', price: 'Free', priceClass: 'shipping-eta__option-price--free', carrier: 'usps', minDays: 5, maxDays: 7, type: 'range' },
    { id: 'standard',  name: 'Standard Shipping',              price: '$9.99',  carrier: 'usps',  minDays: 5, maxDays: 7, type: 'range' },
    { id: 'priority',  name: 'Priority Shipping',              price: '$20.00', carrier: 'fedex', minDays: 3, maxDays: 4, type: 'range' },
    { id: 'express',   name: 'Express Second Day',             price: '$35.00', carrier: 'fedex', minDays: 2, maxDays: 2, type: 'single' },
    { id: 'overnight', name: 'Overnight Shipping',             price: '$50.00', carrier: 'fedex', minDays: 1, maxDays: 1, type: 'single' },
    { id: 'saturday',  name: 'Saturday Overnight',             price: '$70.00', carrier: 'fedex', minDays: 1, maxDays: 1, type: 'saturday' }
  ];

  var DEFAULT_METHOD = 'standard';
  var CUTOFF_HOUR = 15; // 3 PM
  var COUNTDOWN_WINDOW = 0; // unused — countdown now always visible (kept for backwards compat in case any external code references it)
  var TZ = 'America/Los_Angeles';

  /* ====================================================================
     3. DATE HELPERS
     ==================================================================== */

  function toDateStr(d) {
    var y = d.getFullYear();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + day;
  }

  function isWeekend(d) {
    var day = d.getDay();
    return day === 0 || day === 6;
  }

  function isUSPSBusinessDay(d) {
    if (d.getDay() === 0) return false; // Sunday only — USPS ships/delivers on Saturday
    return USPS_HOLIDAYS.indexOf(toDateStr(d)) === -1;
  }

  function isFedExBusinessDay(d) {
    if (isWeekend(d)) return false;
    var ds = toDateStr(d);
    if (FEDEX_CLOSED.indexOf(ds) !== -1) return false;
    if (FEDEX_MODIFIED.indexOf(ds) !== -1) return false; // conservative
    return true;
  }

  function isBusinessDay(d, carrier) {
    return carrier === 'usps' ? isUSPSBusinessDay(d) : isFedExBusinessDay(d);
  }

  /** Ship days exclude Saturday for ALL carriers — we don't ship on Saturdays */
  function isShipDay(d, carrier) {
    if (isWeekend(d)) return false; // Sat + Sun excluded for shipping
    if (carrier === 'usps') return USPS_HOLIDAYS.indexOf(toDateStr(d)) === -1;
    // FedEx
    var ds = toDateStr(d);
    if (FEDEX_CLOSED.indexOf(ds) !== -1) return false;
    if (FEDEX_MODIFIED.indexOf(ds) !== -1) return false;
    return true;
  }

  function nextShipDay(d, carrier) {
    var r = new Date(d);
    do { r = addDays(r, 1); } while (!isShipDay(r, carrier));
    return r;
  }

  function addDays(d, n) {
    var r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
  }

  /** Add N business days from start date for a given carrier */
  function addBusinessDays(start, count, carrier) {
    var d = new Date(start);
    var added = 0;
    while (added < count) {
      d = addDays(d, 1);
      if (isBusinessDay(d, carrier)) added++;
    }
    return d;
  }

  /* ====================================================================
     4. TIMEZONE / CUTOFF HELPERS
     ==================================================================== */

  /** Get current time in LA timezone */
  function getNowLA() {
    var now = new Date();
    var laStr = now.toLocaleString('en-US', { timeZone: TZ });
    return new Date(laStr);
  }

  /** Get today's date object (midnight) in LA */
  function getTodayLA() {
    var la = getNowLA();
    return new Date(la.getFullYear(), la.getMonth(), la.getDate());
  }

  /** Is current LA time before cutoff? */
  function isBeforeCutoff() {
    var la = getNowLA();
    return la.getHours() < CUTOFF_HOUR;
  }

  /** Milliseconds until cutoff today (returns negative if past) */
  function msUntilCutoff() {
    var la = getNowLA();
    var cutoff = new Date(la.getFullYear(), la.getMonth(), la.getDate(), CUTOFF_HOUR, 0, 0);
    return cutoff - la;
  }

  /** Always-positive ms until the NEXT 3 PM PST cutoff (today's if not yet
   *  passed, otherwise tomorrow's). Used by the looping countdown so the
   *  timer naturally resets at the cutoff and starts ticking down to the
   *  next one. */
  function msUntilNextCutoff() {
    var ms = msUntilCutoff();
    if (ms > 0) return ms;
    // Past today's cutoff → ms until tomorrow's 3 PM PST.
    return ms + 24 * 60 * 60 * 1000;
  }

  /** Is today a valid ship day? (used for same-day dispatch countdown) */
  function isTodayShipDay(carrier) {
    return isShipDay(getTodayLA(), carrier);
  }

  /* ====================================================================
     5. SHIP DATE CALCULATION
     ==================================================================== */

  /** Determine when the order ships */
  function getShipDate(carrier) {
    var today = getTodayLA();
    // If today is a valid ship day AND before cutoff → ships today
    if (isShipDay(today, carrier) && isBeforeCutoff()) {
      return today;
    }
    // Otherwise, next valid ship day (Mon–Fri, excluding holidays)
    return nextShipDay(today, carrier);
  }

  /* ====================================================================
     6. ETA CALCULATION PER METHOD
     ==================================================================== */

  function formatDate(d) {
    var days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return days[d.getDay()] + ', ' + months[d.getMonth()] + ' ' + d.getDate();
  }

  function formatDateShort(d) {
    var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return months[d.getMonth()] + ' ' + d.getDate();
  }

  function getMethodETA(method) {
    // Saturday Overnight — special logic
    if (method.type === 'saturday') {
      return getSaturdayOvernightETA();
    }

    var ship = getShipDate(method.carrier);
    var minDate = addBusinessDays(ship, method.minDays, method.carrier);
    var maxDate = addBusinessDays(ship, method.maxDays, method.carrier);

    if (method.type === 'single') {
      return {
        dateText: 'Get it by ' + formatDate(maxDate),
        methodName: method.name,
        shortEta: 'Get it by ' + formatDate(maxDate),
        eligible: true
      };
    }

    // Range
    if (toDateStr(minDate) === toDateStr(maxDate)) {
      return {
        dateText: 'Get it by ' + formatDate(minDate),
        methodName: method.name,
        shortEta: 'Get it by ' + formatDate(minDate),
        eligible: true
      };
    }

    return {
      dateText: 'Estimated delivery ' + formatDateShort(minDate) + ' – ' + formatDateShort(maxDate),
      methodName: method.name,
      shortEta: 'Estimated delivery ' + formatDateShort(minDate) + ' – ' + formatDateShort(maxDate),
      eligible: true
    };
  }

  /* ====================================================================
     7. SATURDAY OVERNIGHT ELIGIBILITY
     Eligible: Thursday after 3 PM PST through Friday before 3 PM PST
     Ships Friday → delivers Saturday
     ==================================================================== */

  function getSaturdayOvernightETA() {
    var la = getNowLA();
    var dow = la.getDay(); // 0=Sun ... 5=Fri 6=Sat
    var hr = la.getHours();

    var eligible = false;
    // Thursday after cutoff
    if (dow === 4 && hr >= CUTOFF_HOUR) eligible = true;
    // Friday before cutoff
    if (dow === 5 && hr < CUTOFF_HOUR) eligible = true;

    if (!eligible) {
      return {
        dateText: '',
        methodName: '',
        shortEta: 'For Friday orders placed before 3 PM PST',
        eligible: false
      };
    }

    // Ships this Friday, delivers this Saturday
    var today = getTodayLA();
    var daysToFri = (5 - today.getDay() + 7) % 7;
    if (daysToFri === 0 && hr >= CUTOFF_HOUR) daysToFri = 7; // past Friday cutoff
    var friday = addDays(today, daysToFri);
    var saturday = addDays(friday, 1);

    return {
      dateText: 'Get it by ' + formatDate(saturday),
      methodName: 'Saturday Overnight',
      shortEta: 'Get it by ' + formatDate(saturday),
      eligible: true
    };
  }

  /* ====================================================================
     8. COUNTDOWN LOGIC
     Always-on countdown that loops at the daily 3 PM PST cutoff. Before
     today's cutoff the timer counts down to today's 3 PM; after the
     cutoff passes it automatically rolls to tomorrow's 3 PM. The text
     adapts based on whether same-day dispatch is still possible.
     ==================================================================== */

  function shouldShowCountdown(method) {
    // Saturday Overnight has its own special-case timing (Thu-Fri window),
    // and doesn't fit the daily-cutoff model. Hide for that method only.
    if (method.id === 'saturday') return false;
    return true;
  }

  function getCountdownText(method) {
    var ms = msUntilNextCutoff();
    if (ms <= 0) return '';
    var totalMin = Math.floor(ms / 60000);
    var hrs = Math.floor(totalMin / 60);
    var mins = totalMin % 60;
    var time;
    if (hrs > 0) {
      time = hrs + ' hr' + (hrs > 1 ? 's' : '') + ' ' + mins + ' min' + (mins !== 1 ? 's' : '');
    } else {
      time = mins + ' min' + (mins !== 1 ? 's' : '');
    }
    // "Same-day dispatch" is accurate only when today is a ship day for
    // this carrier AND we're still before today's cutoff. After cutoff
    // (or on weekends/holidays), the order can't ship today — say so.
    var carrier = method && method.carrier ? method.carrier : 'usps';
    var sameDay = isBeforeCutoff() && isTodayShipDay(carrier);
    var label = sameDay ? 'same-day dispatch' : 'next-day dispatch';
    return 'Order within <span class="shipping-eta__countdown-time">' + time + '</span> for ' + label;
  }

  /* ====================================================================
     9. UI LOGIC
     ==================================================================== */

  function init() {
    var root = document.getElementById('shipping-eta');
    if (!root) return;

    var textEl = document.getElementById('shipping-eta-text');
    var countdownEl = document.getElementById('shipping-eta-countdown');
    var toggleBtn = document.getElementById('shipping-eta-toggle');
    var optionsPanel = document.getElementById('shipping-eta-options');
    var listEl = document.getElementById('shipping-eta-list');
    var tooltipBtn = document.getElementById('shipping-eta-tooltip-btn');
    var tooltip = document.getElementById('shipping-eta-tooltip');

    var selectedId = DEFAULT_METHOD;
    var expanded = false;
    var countdownTimer = null;

    /* --- Build option rows --- */
    function buildOptions() {
      var html = '';
      METHODS.forEach(function (m) {
        var eta = getMethodETA(m);
        var disabled = !eta.eligible;
        var selected = m.id === selectedId;

        html += '<div class="shipping-eta__option' +
          (selected ? ' is-selected' : '') +
          (disabled ? ' is-disabled' : '') +
          '" data-method="' + m.id + '">' +
          '<div class="shipping-eta__radio"><div class="shipping-eta__radio-dot"></div></div>' +
          '<div class="shipping-eta__option-info">' +
            '<span class="shipping-eta__option-name">' + m.name + '</span>' +
            '<span class="shipping-eta__option-eta">' + eta.shortEta + '</span>' +
            (disabled && m.type === 'saturday' ? '<span class="shipping-eta__option-note">Currently unavailable</span>' : '') +
          '</div>' +
          '<span class="shipping-eta__option-price ' + (m.priceClass || '') + '">' + m.price + '</span>' +
        '</div>';
      });
      listEl.innerHTML = html;
    }

    /* --- Update primary text --- */
    function updatePrimary() {
      var method = METHODS.find(function (m) { return m.id === selectedId; });
      if (!method) return;
      var eta = getMethodETA(method);
      textEl.innerHTML = eta.dateText + '<span class="shipping-eta__method"> with ' + eta.methodName + '</span>';

      // Countdown: shown always (except Saturday Overnight which has its
      // own special-case timing). Resets/loops at the daily 3 PM cutoff.
      if (shouldShowCountdown(method)) {
        countdownEl.innerHTML = getCountdownText(method);
        countdownEl.style.display = '';
        startCountdown(method);
      } else {
        countdownEl.style.display = 'none';
        stopCountdown();
      }
    }

    /* --- Countdown timer ---
       Ticks every 30s. Tracks whether we cross the daily 3 PM cutoff —
       when that happens, recalculate the primary ETA (today → tomorrow)
       so the date shown above the countdown stays accurate. */
    function startCountdown(method) {
      stopCountdown();
      var wasBeforeCutoff = isBeforeCutoff();
      countdownTimer = setInterval(function () {
        if (!shouldShowCountdown(method)) {
          countdownEl.style.display = 'none';
          stopCountdown();
          return;
        }
        var nowBefore = isBeforeCutoff();
        if (wasBeforeCutoff && !nowBefore) {
          // We just crossed today's cutoff → primary ETA needs to refresh.
          // updatePrimary will re-render the date + restart this timer.
          updatePrimary();
          return;
        }
        wasBeforeCutoff = nowBefore;
        countdownEl.innerHTML = getCountdownText(method);
      }, 30000); // update every 30s
    }

    function stopCountdown() {
      if (countdownTimer) {
        clearInterval(countdownTimer);
        countdownTimer = null;
      }
    }

    /* --- Selection handler --- */
    function selectMethod(id) {
      var method = METHODS.find(function (m) { return m.id === id; });
      if (!method) return;
      var eta = getMethodETA(method);
      if (!eta.eligible) return;

      selectedId = id;
      updatePrimary();

      // Update UI
      listEl.querySelectorAll('.shipping-eta__option').forEach(function (row) {
        row.classList.toggle('is-selected', row.getAttribute('data-method') === id);
      });
    }

    /* --- Expand / collapse --- */
    function toggle() {
      expanded = !expanded;
      optionsPanel.style.display = expanded ? '' : 'none';
      toggleBtn.classList.toggle('is-expanded', expanded);
    }

    /* --- Event listeners --- */
    toggleBtn.addEventListener('click', function (e) {
      e.preventDefault();
      toggle();
    });

    listEl.addEventListener('click', function (e) {
      var row = e.target.closest('.shipping-eta__option');
      if (!row || row.classList.contains('is-disabled')) return;
      selectMethod(row.getAttribute('data-method'));
    });

    /* Tooltip: click/tap toggles, hover shows/hides on desktop */
    tooltipBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      tooltip.classList.toggle('is-visible');
    });
    tooltipBtn.addEventListener('mouseenter', function () {
      tooltip.classList.add('is-visible');
    });
    tooltipBtn.addEventListener('mouseleave', function () {
      tooltip.classList.remove('is-visible');
    });
    document.addEventListener('click', function () {
      tooltip.classList.remove('is-visible');
    });

    /* --- Initialize --- */
    buildOptions();
    updatePrimary();
  }

  /* ====================================================================
     10. BOOT
     ==================================================================== */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('#shipping-eta')) init();
  });

})();
